
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { BikeRide } from "@/api/users/types";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { Bike, Calendar, Clock, Ruler } from "lucide-react";

interface DashboardFilters {
  regionId: string;
  organizationId: string;
  siteId: string;
}

interface RecentRidesTableProps {
  filters?: DashboardFilters;
}

const RecentRidesTable: React.FC<RecentRidesTableProps> = ({ filters }) => {
  const [rides, setRides] = useState<BikeRide[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRecentRides = async () => {
      setIsLoading(true);

      try {
        const { data, error } = await supabase
          .from('rides')
          .select(`
            id,
            user_id,
            booking_id,
            actual_start_time,
            actual_end_time,
            distance_traveled_km,
            created_at,
            count_towards_offset,
            users (username),
            bookings (
              bike_id,
              start_location_type,
              start_location_id,
              end_location_type,
              end_location_id,
              bikes (
                id,
                model_id,
                serial_number,
                site_id,
                model:bike_models(name),
                sites (
                  name,
                  organization_id,
                  organizations (
                    id,
                    region_id
                  )
                )
              )
            )
          `)
          .order('created_at', { ascending: false })
          .limit(5);

        if (error) throw error;

        // Filter the data based on the provided filters
        let filteredData = data;
        
        if (filters?.siteId && filters.siteId !== 'all') {
          filteredData = filteredData.filter(ride => 
            ride.bookings?.bikes?.site_id === filters.siteId
          );
        } else if (filters?.organizationId && filters.organizationId !== 'all') {
          filteredData = filteredData.filter(ride => 
            ride.bookings?.bikes?.sites?.organization_id === filters.organizationId
          );
        } else if (filters?.regionId && filters.regionId !== 'all') {
          filteredData = filteredData.filter(ride => 
            ride.bookings?.bikes?.sites?.organizations?.region_id === filters.regionId
          );
        }

        // Limit to 5 after filtering
        filteredData = filteredData.slice(0, 5);

        const formattedRides = filteredData.map((ride: any): BikeRide => {
          const startTime = ride.actual_start_time ? new Date(ride.actual_start_time) : new Date(ride.created_at);
          const endTime = ride.actual_end_time ? new Date(ride.actual_end_time) : new Date();
          const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));
          
          const bikeId = ride.bookings?.bike_id || 'unknown';
          const serialNumber = ride.bookings?.bikes?.serial_number || 'unknown';
          const modelId = ride.bookings?.bikes?.model_id || null;
          const bikeName = ride.bookings?.bikes?.model?.name || 'Unknown Model';
          
          const startLocation = ride.bookings?.start_location_type === 'site' ? 'Site' : 'Home';
          const endLocation = ride.bookings?.end_location_type === 'site' ? 'Site' : 'Home';
          
          return {
            id: ride.id,
            bikeId: bikeId,
            serialNumber: serialNumber,
            modelId: modelId,
            bikeName: bikeName,
            userId: ride.user_id,
            userName: ride.users?.username || 'Unknown User',
            startTime,
            endTime,
            startLocation,
            endLocation,
            distance: ride.distance_traveled_km || 0,
            duration: durationMinutes,
            countTowardsOffset: ride.count_towards_offset !== false
          };
        });

        setRides(formattedRides);
      } catch (error) {
        console.error('Error fetching recent rides:', error);
        toast.error('Failed to load recent rides');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentRides();
  }, [filters]);

  const formatDate = (date: Date): string => {
    return format(date, 'MMM d, h:mm a');
  };

  return (
    <div className="w-full overflow-hidden">
      <h3 className="text-lg font-medium pb-3">Recent Rides</h3>
      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20">
              <TableHead className="w-[120px]"><div className="flex items-center gap-1"><Bike className="h-3 w-3" /> eBike</div></TableHead>
              <TableHead><div className="flex items-center gap-1">User</div></TableHead>
              <TableHead><div className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Date</div></TableHead>
              <TableHead><div className="flex items-center gap-1"><Ruler className="h-3 w-3" /> Distance</div></TableHead>
              <TableHead><div className="flex items-center gap-1"><Clock className="h-3 w-3" /> Duration</div></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10">Loading...</TableCell>
              </TableRow>
            ) : rides.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10">No recent rides found.</TableCell>
              </TableRow>
            ) : (
              rides.map((ride) => (
                <TableRow key={ride.id} className="hover:bg-muted/20 transition-colors">
                  <TableCell className="py-3">
                    <div className="flex flex-col">
                      <Link to={`/bikes/${ride.bikeId}`} className="text-primary hover:underline font-medium">
                        {ride.bikeName}
                      </Link>
                      <span className="text-xs text-muted-foreground">{ride.serialNumber ? ride.serialNumber.substring(0, 6) : 'Unknown'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3">
                    <Link to={`/users/${ride.userId}`} className="hover:underline">
                      {ride.userName}
                    </Link>
                  </TableCell>
                  <TableCell className="py-3">{formatDate(ride.startTime)}</TableCell>
                  <TableCell className="py-3 font-medium">{ride.distance.toFixed(1)} km</TableCell>
                  <TableCell className="py-3">{ride.duration} min</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default RecentRidesTable;
